import * as z from 'zod';
import * as ba from './base';
import { SCRIPT_SCHEMA } from '../scripting';
import { ACTION } from './base_schemas';
import { isComponentContext, assertVueComponent } from '../helpers';
import { IModalResult } from '../modal';

export const MODAL_RESULT_SCHEMA = z.object({
  close: z.boolean().optional(),
  ok: z.union([z.boolean(), SCRIPT_SCHEMA()]).optional(),
  cancel: z.union([z.boolean(), SCRIPT_SCHEMA()]).optional(),
  value: z.any().optional(),
  maxInstancesPerComponentExceeded: z.boolean().optional()
});

export interface CloseModalActionArgs {
  name?: string;
  result?: IModalResult;
}

export class CloseModalAction extends ba.Action<CloseModalActionArgs, any> {
  static get manifest() {
    return {
      name: 'closeModal',
      schema: z
        .object({
          name: z.union([z.string(), SCRIPT_SCHEMA()]).optional(),
          result: MODAL_RESULT_SCHEMA.optional()
        })
        .optional()
    };
  }

  async handle(event: ba.ActionEvent, args: CloseModalActionArgs | null) {
    let modalName = args && args.name;
    if (!modalName) {
      if (
        !isComponentContext(event.component) ||
        !event.component.isModalComponent
      ) {
        throw new Error(
          'Error executing closeModal action: modalName is not specified ' +
            'and the current scope is not a modal component.'
        );
      }
      modalName = event.component.modalName;
    }

    if (args && modalName) {
      assertVueComponent(event.component);
      return event.component.$imodal.close(modalName, args.result || {});
    }

    if (modalName) {
      assertVueComponent(event.component);
      return event.component.$imodal.close(modalName, {});
    }

    throw new Error(
      'Error executing closeModal action: could not resolve modal name.'
    );
  }
}

export interface ShowModalActionArgs {
  name: string;
  props: Dictionary<any>;
}

export class ShowModalAction extends ba.Action<ShowModalActionArgs, any> {
  static get manifest() {
    return {
      name: 'showModal',
      schema: z.object({
        name: z.union([z.string(), SCRIPT_SCHEMA()]),
        props: z.record(z.string(), z.any()).optional()
      })
    };
  }

  async handle(event: ba.ActionEvent, args: ShowModalActionArgs) {
    assertVueComponent(event.component);
    const modalResult = await event.component.$imodal.show(args);

    if (modalResult?.error) {
      await event.component.$imodal.error({ error: modalResult.error });
    } else {
      await this.dispatch(
        modalResult && modalResult.cancel ? 'cancel' : 'close',
        {
          modalResult
        }
      );
    }

    return modalResult;
  }
}
